import { useCallback, useEffect, useMemo, useState } from "react";
import { getLabels } from "../../services/helpdeskWorkflowService";

// ─── Etiquetas del helpdesk (caché de sesión compartida) ─────────────────────
// La lista de etiquetas del tenant la consumen la cabecera del detalle, el
// selector, la lista de conversaciones, los filtros y los ajustes. Se pide UNA
// vez por sesión: todas las instancias del hook comparten la misma promesa y el
// mismo resultado, y `reload()` invalida la caché para todas (tras crear,
// renombrar o borrar una etiqueta).
//
// En error se conserva lo anterior (mismo criterio que las carpetas): un fallo
// transitorio no puede vaciar los chips que el agente ya estaba viendo. Un
// 404/405 es el endpoint sin desplegar: se trata como "sin etiquetas".

let cache = null; // Array<label> | null
let inflight = null; // Promise | null
let lastError = null;
const listeners = new Set();
// Referencia estable para "sin datos": así `byId` no se recalcula por render.
const EMPTY = [];

function notify() {
  listeners.forEach((fn) => fn());
}

function fetchShared(force) {
  if (inflight) return inflight;
  if (cache && !force) return Promise.resolve(cache);
  inflight = getLabels()
    .then((list) => {
      cache = Array.isArray(list) ? list : [];
      lastError = null;
      return cache;
    })
    .catch((err) => {
      if (err?.status === 404 || err?.status === 405) {
        cache = cache ?? [];
        lastError = null;
      } else {
        console.warn("Etiquetas no disponibles:", err);
        lastError = err;
      }
      return cache;
    })
    .finally(() => {
      inflight = null;
      notify();
    });
  notify();
  return inflight;
}

/** Solo para tests: deja la caché como recién cargada la app. */
export function resetHelpdeskLabelsCache() {
  cache = null;
  inflight = null;
  lastError = null;
  notify();
}

/**
 * @returns {{
 *   labels: Array<{id:string,name:string,color:string|null,description?:string,count?:number}>,
 *   byId: Record<string, object>,
 *   reload: () => Promise<Array<object>>,
 *   loading: boolean,
 *   error: Error|null,
 * }}
 */
export function useHelpdeskLabels() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((n) => n + 1);
    listeners.add(listener);
    if (!cache && !inflight) fetchShared(false);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const labels = cache ?? EMPTY;
  const byId = useMemo(
    () => Object.fromEntries(labels.map((l) => [l.id, l])),
    [labels],
  );
  const reload = useCallback(() => fetchShared(true), []);

  return {
    labels,
    byId,
    reload,
    // "Cargando" solo mientras no hay nada que mostrar: un refetch con la
    // lista ya en pantalla no debe parpadear a esqueleto.
    loading: !!inflight && cache === null,
    error: lastError,
  };
}

export default useHelpdeskLabels;
